import React from 'react';
import {ComposedChart, Line, Area, XAxis, ResponsiveContainer, LabelList, CartesianGrid} from 'recharts';
import {BIOMARKER_STATUS_COLORS} from '../../constants/colors';

interface TestingHistoryDataPoint {
    name: string;
    value: number | null;
    dummy?: boolean;
}

interface TestingHistoryProps {
    data: TestingHistoryDataPoint[];
    title?: string;
    height?: number;
    showGrid?: boolean;
    showVerticalLines?: boolean;
    simpleMode?: boolean;
    thresholds?:
        | {
              low: number;
              high: number;
          }
        | {
              excellent: number;
              good: number;
          };
    className?: string;
    labelFontSize?: string;
}

const TestingHistory: React.FC<TestingHistoryProps> = ({
    data,
    title = 'Testing History',
    height = 200,
    showGrid = true,
    showVerticalLines = false,
    simpleMode = false,
    thresholds,
    className = '',
    labelFontSize = '16px',
}) => {
    // Custom dot component that colors based on value and thresholds
    const CustomDot = (props: any) => {
        const {cx, cy, payload} = props;

        // Don't render dots for dummy data points
        if (payload.dummy) return null;

        let color: string = BIOMARKER_STATUS_COLORS.NORMAL; // Default green for normal values

        if (thresholds && payload.value !== null) {
            if ('excellent' in thresholds) {
                // Heart health 3-tier system
                if (payload.value >= thresholds.excellent) {
                    color = BIOMARKER_STATUS_COLORS.NORMAL; // Green for excellent
                } else if (payload.value >= thresholds.good) {
                    color = BIOMARKER_STATUS_COLORS.LOW; // Orange for good
                } else {
                    color = '#EF4444'; // Red for poor
                }
            } else {
                // Reference range defines normal values
                if (payload.value < thresholds.low) {
                    color = BIOMARKER_STATUS_COLORS.LOW; // Orange for low (below range)
                } else if (payload.value > thresholds.high) {
                    color = BIOMARKER_STATUS_COLORS.HIGH; // Pink for high (above range)
                } else {
                    color = BIOMARKER_STATUS_COLORS.NORMAL; // Green for normal (within range)
                }
            }
        }

        return <circle cx={cx} cy={cy} r={8} fill={color} stroke="none" />;
    };

    // Custom label component for values above circles
    const CustomLabel = (props: any) => {
        const {x, y, value, payload} = props;

        // Don't render labels for dummy data points
        if (payload && payload.dummy) return null;
        if (value === null) return null;

        // Always use navy color for value labels
        const color = '#334155'; // Lighter navy color for all value labels

        return (
            <text
                x={x}
                y={y - 15}
                fill={color}
                textAnchor="middle"
                style={{
                    fontFamily: 'Poppins',
                    fontSize: labelFontSize,
                    fontWeight: 400,
                    lineHeight: 'auto',
                    letterSpacing: '0%',
                }}>
                {typeof value === 'number'
                    ? (() => {
                          const rounded = Number(Number(value).toFixed(2));
                          return rounded === Math.floor(rounded) ? rounded.toString() : rounded.toString();
                      })()
                    : value}
            </text>
        );
    };

    return (
        <div className={`${className}`}>
            {title && (
                <h3
                    className="text-[24px] font-normal text-mld-card-text mb-4 text-center"
                    style={{fontFamily: 'Poppins', fontWeight: 400, lineHeight: 'auto', letterSpacing: '0%'}}>
                    {title}
                </h3>
            )}

            <div className="relative">
                <div className="w-full">
                    <ResponsiveContainer width="100%" height={height}>
                        <ComposedChart
                            data={data}
                            margin={
                                simpleMode
                                    ? {top: 20, right: 20, left: 20, bottom: 0}
                                    : {top: 40, right: 30, left: 30, bottom: 0}
                            }>
                            {/* Recharts grid - like staging */}
                            {showGrid && (
                                <CartesianGrid
                                    strokeDasharray="0"
                                    stroke="#E9E9E4"
                                    strokeWidth={1}
                                    vertical={showVerticalLines}
                                    horizontal={true}
                                />
                            )}

                            <XAxis
                                dataKey="name"
                                tick={{fill: '#013B4B', fontSize: 12}}
                                axisLine={false}
                                tickLine={false}
                                interval={0}
                            />

                            {/* Blue gradient area fill */}
                            <defs>
                                <linearGradient id="heartHealthGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.05} />
                                </linearGradient>
                            </defs>

                            <Area
                                type="monotone"
                                dataKey="value"
                                stroke="none"
                                fill="url(#heartHealthGradient)"
                                connectNulls={false}
                            />

                            <Line
                                type="monotone"
                                dataKey="value"
                                stroke={simpleMode ? '#42D4B0' : '#334155'}
                                strokeWidth={simpleMode ? 2 : 1}
                                dot={simpleMode ? {r: 4, fill: '#42D4B0'} : <CustomDot />}
                                activeDot={simpleMode ? {r: 6, fill: '#42D4B0'} : false}>
                                {!simpleMode && <LabelList content={<CustomLabel />} />}
                            </Line>
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default TestingHistory;
